import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Stethoscope, ShieldCheck, Heart, Lock, Check } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { BrandLockup } from "@/components/BrandMark";
import { useSEO } from "@/lib/seo";

/**
 * Phase 1 Therapists page.
 *
 * Pro tier UX is not yet selectable (pricing/plumbing not finalized for
 * professional billing). The page stays live for SEO and to capture early
 * interest via a waitlist email form. When Pro launches, this page flips
 * back to a full sales page with sign-up CTAs.
 */
export default function Therapists() {
  const [items, setItems] = useState([]);
  const [stateFilter, setStateFilter] = useState("");

  // Waitlist form state
  const [email, setEmail] = useState("");
  const [credentials, setCredentials] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  useSEO({
    title: "AAC for SLPs and clinicians — join the waitlist",
    description:
      "SonSpeak Professional is coming soon for SLPs, therapists, and school clinicians. Join the waitlist for early access.",
    path: "/therapists",
  });

  useEffect(() => {
    (async () => {
      try {
        const params = stateFilter ? `?state=${encodeURIComponent(stateFilter)}` : "";
        const { data } = await api.get(`/therapists${params}`);
        setItems(data?.items || []);
      } catch {
        /* empty list ok */
      }
    })();
  }, [stateFilter]);

  const submitWaitlist = async (e) => {
    e.preventDefault();
    setErr("");
    if (!email.trim()) {
      setErr("Email is required.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/therapists/waitlist", {
        email: email.trim(),
        credentials: credentials.trim() || null,
      });
      setDone(true);
      setEmail("");
      setCredentials("");
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen dotted-bg">
      <header className="px-5 sm:px-10 py-5 border-b-[3px] border-[color:var(--ink)] flex items-center justify-between bg-[color:var(--cream)]">
        <BrandLockup size={180} />
        <Link to="/register" className="pill-btn primary">
          <ArrowRight size={14} /> Sign up
        </Link>
      </header>

      <main className="max-w-6xl mx-auto px-5 sm:px-10 py-12">
        {/* Hero — no live pricing, no Sign-up CTA */}
        <section className="grid lg:grid-cols-[1.4fr_1fr] gap-8 mb-16 items-start">
          <div>
            <div className="display text-xs uppercase tracking-[0.3em] text-[color:var(--coral-deep)] font-bold mb-3">
              For SLPs &amp; clinicians
            </div>
            <h1 className="display text-4xl sm:text-6xl font-extrabold leading-tight mb-5">
              The AAC tool you actually want your clients to take home.
            </h1>
            <p className="text-lg text-[color:var(--ink-soft)] mb-6">
              SonSpeak Professional is coming soon — a clinician dashboard built for the way you
              actually work, including a way to extend supported access to families who can't afford
              a paid plan.
            </p>
            <p className="text-base text-[color:var(--ink-soft)]">
              Join the waitlist and we’ll email you when Professional opens up.
            </p>
          </div>

          {/* Waitlist card */}
          <div className="brutal-card bg-white p-5" data-testid="therapists-waitlist-card">
            <div className="display text-xs uppercase tracking-[0.2em] text-[color:var(--ink-soft)] font-bold mb-1 flex items-center gap-2">
              <Lock size={14} /> Coming soon
            </div>
            <div className="display text-2xl font-extrabold mb-3">SonSpeak Professional</div>
            <ul className="space-y-2 text-sm mb-4">
              <li>✅ Multi-client dashboard + per-client reports</li>
              <li>✅ Push board templates with one share link</li>
              <li>✅ A way to extend supported access to qualifying families</li>
              <li>✅ CSV / PDF usage snapshots for caregiver conversations</li>
            </ul>
            {done ? (
              <div
                className="brutal-card bg-[#D6F5C8] p-3 text-sm flex items-start gap-2"
                data-testid="therapists-waitlist-success"
              >
                <Check size={16} className="mt-0.5 text-[color:var(--ink)] shrink-0" />
                <div>
                  <strong>You’re on the list.</strong> We’ll email you the moment Professional
                  opens. No spam.
                </div>
              </div>
            ) : (
              <form onSubmit={submitWaitlist} className="space-y-2" data-testid="therapists-waitlist-form">
                <input
                  type="email"
                  placeholder="you@clinic.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="brutal-input w-full"
                  required
                  data-testid="therapists-waitlist-email"
                />
                <input
                  type="text"
                  placeholder="Credentials (optional — e.g. SLP-CCC)"
                  value={credentials}
                  onChange={(e) => setCredentials(e.target.value)}
                  className="brutal-input w-full"
                  maxLength={120}
                  data-testid="therapists-waitlist-credentials"
                />
                {err && (
                  <div className="text-xs text-[color:var(--coral-deep)]" data-testid="therapists-waitlist-error">
                    {err}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={busy}
                  className="pill-btn primary w-full"
                  data-testid="therapists-waitlist-submit"
                >
                  {busy ? "Saving…" : "Join the waitlist"}
                </button>
              </form>
            )}
          </div>
        </section>

        {/* Public directory of already-listed clinicians (existing feature, kept) */}
        <section data-testid="therapist-directory">
          <div className="flex flex-wrap items-center gap-3 mb-5">
            <h2 className="display text-2xl sm:text-3xl font-extrabold flex-1">
              Find a SonSpeak clinician
            </h2>
            <input
              type="text"
              placeholder="Filter by state (e.g. CA)"
              value={stateFilter}
              onChange={(e) => setStateFilter(e.target.value)}
              className="brutal-input"
              data-testid="therapists-state-filter"
            />
          </div>

          {items.length === 0 ? (
            <div className="brutal-card bg-white p-6 text-center text-[color:var(--ink-soft)]">
              <ShieldCheck size={28} className="mx-auto mb-2" />
              No clinicians listed yet. Each profile is hand-reviewed by SonSpeak before
              going live.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {items.map((t) => (
                <Link
                  key={t.slug}
                  to={`/therapists/${t.slug}`}
                  className="brutal-card bg-white p-4 hover:bg-[color:var(--paper-muted)] transition-colors"
                  data-testid={`therapist-card-${t.slug}`}
                >
                  <div className="flex items-start gap-2 mb-2">
                    <Stethoscope size={18} className="text-[color:var(--coral-deep)] mt-1" />
                    <div className="flex-1 min-w-0">
                      <div className="display text-base font-extrabold">{t.full_name}</div>
                      <div className="text-xs text-[color:var(--ink-soft)]">
                        {t.credentials} · {t.license_state}
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-[color:var(--ink-soft)] line-clamp-3">{t.bio}</p>
                  {t.sliding_scale && (
                    <span className="inline-block mt-2 text-[10px] px-2 py-0.5 rounded-full border-[2px] border-[color:var(--ink)] bg-[color:var(--gold)] font-bold uppercase tracking-[0.15em]">
                      <Heart size={10} className="inline -mt-0.5 mr-1" />
                      Sliding scale
                    </span>
                  )}
                </Link>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
